import { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  API,
  Field2 as Field,
} from 'vibeguide';
import {
  isEmpty,
} from 'lodash';
import Grid from '@mui/material/Grid';

function Information({
  user,
  firstName = '',
  lastName = '',
  companyName = '',
  companyId = '',
  companyId: currCompanyId = '',
  partnerName = '',
  email = '',
  title = '',
  phone = '',
  systemIntegrator,
  isIntegrationAccount,
  disableInput = false,
  disableCompany = false,
  permissionPrefix,
  tags = [],
  isNew = false,
  isPartner = false,
  onUpdate = () => {},
}) {
  const {
    ADMIN_COMPANY_ID: adminCompanyId,
  } = process.env;

  const onChange = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    onUpdate({
      [name]: value,
    });
  };

  const onSetCompany = ({ id: companyId, value: companyName }) => {
    const data = !companyId
      ? {
        allLocations: false,
        locations: [],
        locationsData: {
          companies: [],
          locations: [],
        },
        tags: currCompanyId !== companyId
          ? tags.filter(tag => tag.type === 'admin')
          : tags,
      }
      : {};

    if (!isEmpty(companyName) || companyName === '' || isNew) {
      onUpdate({
        companyId,
        companyName,
        ...data,
      });
    }
  };

  const onChangePartnerField = (data) => {
    const {
      id,
      value,
    } = data;

    const useData = {};

    if (id) {
      // use admin company ID to allow assigning ACAF company locations
      useData.companyId = adminCompanyId;
    } else {
      // no partner ID, reset locations data
      useData.companyId = '';
      useData.allLocations = false;
      useData.locations = [];
      useData.locationsData = {
        companies: [],
        locations: [],
      };
    }

    onUpdate({
      partnerId: id,
      partnerName: value,
      ...useData,
    });
  };

  const onChangeTag = (data) => {
    onUpdate(data);
  };

  /**
   * User can't list partners and is creating a new partner
   */
  useEffect(() => {
    if (isPartner && isNew && !user.can('partner.list')) {
      // Set the partner to the users partner
      onUpdate({
        partnerId: user.partnerId,
        partnerName: user.partnerName,
      });
    }
  }, []);

  return (
    <div className="Information info-user">
      {isIntegrationAccount ? (
        <Grid spacing={2} container>
          <Grid size={6}>
            <Field
              type="text"
              label="System Integrator"
              name="sysIntegrator"
              placeholder="System Integrator"
              value={systemIntegrator}
              tabIndex={1}
              disabled={disableInput}
              testId="user-system-integrator-input"
              onChange={onChange}
              required
              autoFocus
            />
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid spacing={2} container>
            <Grid size={6}>
              <Field
                type="text"
                label="First Name"
                name="fname"
                placeholder="First Name"
                value={firstName}
                tabIndex={1}
                disabled={disableInput}
                testId="user-first-name-input"
                onChange={onChange}
                required
                autoFocus
              />
            </Grid>

            <Grid size={6}>
              <Field
                type="text"
                label="Last Name"
                name="lname"
                placeholder="Last Name"
                value={lastName}
                tabIndex={2}
                disabled={disableInput}
                testId="user-last-name-input"
                onChange={onChange}
                required
              />
            </Grid>

            <Grid size={6}>
              {!isPartner ? (
                <Field
                  type="company"
                  label="Company"
                  name="companyName"
                  value={companyName}
                  tabIndex={3}
                  companyProps={{
                    companyId,
                    companyName,
                  }}
                  disabled={disableCompany || disableInput}
                  testId="user-company-input"
                  onChange={onSetCompany}
                  required
                />
              ) : (
                <Field
                  type="dropdown"
                  label="Partner"
                  name="partnerName"
                  value={partnerName || ''}
                  tabIndex={3}
                  dropdownProps={{
                    type: 'partner',
                    attr: 'name',
                    fetch: API.Partner.list,
                  }}
                  disabled={disableInput || !user.can('partner.view') || !isNew}
                  testId="user-partner-dropdown"
                  onChange={onChangePartnerField}
                  required
                />
              )}
            </Grid>

            <Grid size={6}>
              <Field
                type="text"
                label="Email"
                name="email"
                placeholder="user@example.com"
                value={email}
                tabIndex={4}
                disabled={disableInput}
                testId="user-email-input"
                onChange={onChange}
                required
              />
            </Grid>

            <Grid size={6}>
              <Field
                type="text"
                label="Title"
                name="title"
                placeholder="Title"
                value={title}
                tabIndex={5}
                disabled={disableInput}
                testId="user-title-input"
                onChange={onChange}
                required={isPartner}
              />
            </Grid>

            <Grid size={6}>
              <Field
                type="phone"
                label="Phone"
                name="phone"
                value={phone}
                tabIndex={6}
                disabled={disableInput}
                testId="user-phone-input"
                onChange={onChange}
              />
            </Grid>

            <Grid size={12}>
              <Field
                type="tags"
                label="Tags"
                name="tags"
                tagProps={{
                  companyId,
                  tags,
                  allowTypes: !isPartner
                    ? ['client', 'admin']
                    // only allow admin tags for partner users
                    : ['admin'],
                  showAs: 'list',
                  assign: user.can(`${permissionPrefix}.assign_tag`),
                }}
                disabled={disableInput}
                testId="user-tags-input"
                onChange={onChangeTag}
                tabIndex={7}
              />
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
}

Information.propTypes = {
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  companyId: PropTypes.string,
  companyName: PropTypes.string,
  partnerName: PropTypes.string,
  email: PropTypes.string,
  title: PropTypes.string,
  phone: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.object),
  disableInput: PropTypes.bool,
  disableCompany: PropTypes.bool,
  permissionPrefix: PropTypes.string.isRequired,
  isNew: PropTypes.bool,
  isPartner: PropTypes.bool,
  onUpdate: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

export default connect(mapStateToProps)(Information);
